import React, {
  useEffect,
  useState,
} from "react";

import {
  Button,
  Progress,
  Spin,
  Typography,
} from "antd";

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  HomeOutlined,
  MinusCircleOutlined,
  TrophyOutlined,
} from "@ant-design/icons";

import {
  useParams,
} from "react-router-dom";

import "./exam.css";

const {
  Title,
  Text,
} = Typography;

const EXAM_API_BASE =
  process.env
    .REACT_APP_EXAM_API_BASE_URL ||
  "http://localhost:5000/api/exams";

export default function ExamResultPage() {
  const {
    attemptId,
  } = useParams();

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    result,
    setResult,
  ] = useState(null);

  const [
    error,
    setError,
  ] = useState("");

  const userId =
    localStorage.getItem(
      "exam_user_id"
    );

  useEffect(() => {
    if (!userId) {
      window.location.href =
        "/test-login";

      return;
    }

    loadResult();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  const loadResult =
    async () => {
      try {
        setLoading(true);
        setError("");

        const response =
          await fetch(
            `${EXAM_API_BASE}/attempts/${attemptId}/result?user_id=${encodeURIComponent(
              userId
            )}`
          );

        const data =
          await response.json();

        if (!data?.status) {
          setError(
            data?.message ||
              "Unable to load result"
          );

          return;
        }

        setResult(
          data.data
        );
      } catch (err) {
        console.error(
          "Result load error:",
          err
        );

        setError(
          "Unable to load result"
        );
      } finally {
        setLoading(false);
      }
    };

  // ============================================================
  // FORMAT LEVEL
  // ============================================================

  const formatLevel =
    (level) => {
      if (!level) {
        return "-";
      }

      return String(level)
        .replaceAll("_", " ")
        .replace(
          /\b\w/g,
          (char) =>
            char.toUpperCase()
        );
    };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div className="exam-loading-page">
        <Spin size="large" />

        <div className="exam-loading-text">
          Loading result...
        </div>
      </div>
    );
  }

  // ============================================================
  // ERROR
  // ============================================================

  if (
    error ||
    !result
  ) {
    return (
      <div className="exam-result-error-page">
        <CloseCircleOutlined
          className="exam-result-error-icon"
        />

        <Title level={3}>
          Result Not Available
        </Title>

        <Text>
          {error ||
            "Unable to load your exam result."}
        </Text>

        <Button
          type="primary"
          onClick={
            loadResult
          }
          className="exam-gradient-button exam-result-retry"
        >
          Try Again
        </Button>
      </div>
    );
  }

  // ============================================================
  // SCORE
  // ============================================================

  const percentage =
    Number(
      result.percentage ??
        (
          Number(
            result.total_marks
          ) > 0
            ? Math.round(
                (
                  Number(
                    result.obtained_marks
                  ) /
                  Number(
                    result.total_marks
                  )
                ) *
                  100
              )
            : 0
        )
    );

  return (
    <div className="exam-result-page exam-result-page-v2">
      {/* ======================================================
          HEADER
      ====================================================== */}

      <header className="exam-result-v2-header">
        <div className="exam-result-v2-header-inner">
          <div className="exam-simple-brand-wrap">
            <img
              src="/logo192.png"
              alt="SM-ART"
              className="exam-simple-brand-logo"
            />

            <div>
              <div className="exam-simple-brand-title">
                SM-ART
              </div>

              <div className="exam-simple-brand-sub">
                Assessment Result
              </div>
            </div>
          </div>

          <button
            type="button"
            className="exam-result-v2-home-link"
            onClick={() => {
              window.location.href =
                "/exam";
            }}
          >
            <HomeOutlined />

            <span>
              Assessment Home
            </span>
          </button>
        </div>
      </header>

      {/* ======================================================
          MAIN
      ====================================================== */}

      <main className="exam-result-v2-main">
        {/* ====================================================
            RESULT HEADING
        ==================================================== */}

        <section className="exam-result-v2-heading">
          <div>
            <span className="exam-result-v2-kicker">
              Result Summary
            </span>

            <Title level={1}>
              {result.exam_name}
            </Title>

            <div className="exam-result-v2-heading-meta">
              <span>
                Attempt{" "}
                {result.attempt_no}
              </span>

              <i />

              <span>
                {result.total_questions}{" "}
                Questions
              </span>

              {result.level && (
                <>
                  <i />

                  <span>
                    {formatLevel(
                      result.level
                    )}
                  </span>
                </>
              )}
            </div>
          </div>

          <span className="exam-result-v2-status">
            <CheckCircleOutlined />

            Completed
          </span>
        </section>

        {/* ====================================================
            SCORE
        ==================================================== */}

        <section className="exam-result-v2-score-section">
          <div className="exam-result-v2-score-left">
            <div className="exam-result-v2-score-ring">
              <Progress
                type="circle"
                percent={
                  percentage
                }
                size={172}
                strokeWidth={7}
                strokeColor="#154a78"
                trailColor="#edf1f5"
                format={() => (
                  <div className="exam-result-v2-ring-copy">
                    <strong>
                      {percentage}%
                    </strong>

                    <span>
                      Score
                    </span>
                  </div>
                )}
              />
            </div>

            <div className="exam-result-v2-score-copy">
              <span>
                Assessment Performance
              </span>

              <div className="exam-result-v2-marks">
                <strong>
                  {
                    result.obtained_marks
                  }
                </strong>

                <span>
                  /{" "}
                  {
                    result.total_marks
                  }{" "}
                  marks
                </span>
              </div>

              <p>
                You answered{" "}
                <strong>
                  {
                    result.correct_count
                  }
                </strong>{" "}
                out of{" "}
                <strong>
                  {
                    result.total_questions
                  }
                </strong>{" "}
                questions correctly.
              </p>
            </div>
          </div>

          {/* ==================================================
              SCORE BREAKDOWN
          ================================================== */}

          <div className="exam-result-v2-breakdown">
            <div>
              <span className="is-success">
                <CheckCircleOutlined />
              </span>

              <small>
                Correct
              </small>

              <strong>
                {
                  result.correct_count
                }
              </strong>
            </div>

            <div>
              <span className="is-danger">
                <CloseCircleOutlined />
              </span>

              <small>
                Wrong
              </small>

              <strong>
                {
                  result.wrong_count
                }
              </strong>
            </div>

            <div>
              <span className="is-warning">
                <MinusCircleOutlined />
              </span>

              <small>
                Unanswered
              </small>

              <strong>
                {
                  result.unanswered_count
                }
              </strong>
            </div>

            <div>
              <span className="is-info">
                <TrophyOutlined />
              </span>

              <small>
                Marks
              </small>

              <strong>
                {
                  result.obtained_marks
                }
                /
                {
                  result.total_marks
                }
              </strong>
            </div>
          </div>
        </section>

        {/* ====================================================
            ASSESSMENT DETAILS
        ==================================================== */}

        <section className="exam-result-v2-details">
          <div className="exam-result-v2-section-head">
            <div>
              <strong>
                Assessment Details
              </strong>

              <span>
                Reference information
                for this result
              </span>
            </div>
          </div>

          <div className="exam-result-v2-detail-grid">
            <div>
              <span>
                User ID
              </span>

              <strong>
                {result.user_id}
              </strong>
            </div>

            <div>
              <span>
                Department
              </span>

              <strong>
                {
                  result.department_name ||
                  "Department name not found"
                }
              </strong>
            </div>

            <div>
              <span>
                Level
              </span>

              <strong>
                {formatLevel(
                  result.level
                )}
              </strong>
            </div>

            <div>
              <span>
                Unit Code
              </span>

              <strong>
                {
                  result.unit_code ||
                  "-"
                }
              </strong>
            </div>

            <div>
              <span>
                Unit
              </span>

              <strong>
                {
                  result.unit_name ||
                  "-"
                }
              </strong>
            </div>
          </div>
        </section>

        {/* ====================================================
            ACTIONS
        ==================================================== */}

        <div className="exam-result-v2-actions">
          <button
            type="button"
            className="exam-result-v2-back"
            onClick={() => {
              window.location.href =
                "/exam";
            }}
          >
            <HomeOutlined />

            <span>
              Back to Assessment
            </span>
          </button>
        </div>
      </main>

      {/* ======================================================
          FOOTER
      ====================================================== */}

      <footer className="exam-result-footer">
        SM-ART Smart Manufacturing

        <span>•</span>

        Assessment Portal
      </footer>
    </div>
  );
}
